<template>
  <b-modal
    id="modal-merchant-business-address-add"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="ADD NEW MERCHANT BUSINESS ADDRESS"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="createMerchantBusinessAddressForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickCreateMerchantBusinessAddress)"
        >
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Address Type"
                label-for="h-addressType"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="addressType"
                  rules="required"
                >
                  <v-select
                    v-model="form.addressType"
                    :options="addressTypeOptions"
                    :reduce="option => ({ id: option.id, slug: option.slug })"
                    label="name"
                    placeholder="Select the address type"
                    autocomplete="nope"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Address Line 1"
                label-for="h-addressLine1"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="addressLine1"
                  rules="required"
                >
                  <b-form-input
                    id="addressLine1"
                    v-model="form.addressLine1"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the address line 1"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Address Line 2"
                label-for="h-addressLine2"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="addressLine2"
                  rules=""
                >
                  <b-form-input
                    id="addressLine2"
                    v-model="form.addressLine2"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the address line 2"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Address Line 3"
                label-for="h-addressLine3"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="addressLine3"
                  rules=""
                >
                  <b-form-input
                    id="addressLine3"
                    v-model="form.addressLine3"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the address line 3"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Postal /Zip Code"
                label-for="h-postalCode"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="postalCode"
                  rules="required"
                >
                  <b-form-input
                    id="postalCode"
                    v-model="form.postalCode"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the postal /zip Code"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Country"
                label-for="h-country"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="country"
                  rules="required"
                >
                  <v-select
                    v-model="form.country"
                    :options="countryOptions"
                    :reduce="option => option.id"
                    label="name"
                    placeholder="Select the country"
                    autocomplete="nope"
                    @input="fetchStateList()"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="State"
                label-for="h-state"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="state"
                  rules="required"
                >
                  <v-select
                    v-model="form.state"
                    :options="stateOptions"
                    :reduce="option => option.id"
                    label="name"
                    placeholder="Select the state"
                    autocomplete="nope"
                    @input="fetchCityList()"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="City"
                label-for="h-city"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="city"
                  rules="required"
                >
                  <v-select
                    v-model="form.city"
                    :options="cityOptions"
                    :reduce="option => option.id"
                    label="name"
                    placeholder="Select the city"
                    autocomplete="nope"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <!-- Merchant Location -->
            <b-col cols="6">
              <b-form-group
                label="Location"
                label-for="h-location"
                label-class="text-secondary"
              >
                <base-location-picker
                  v-model="form.merchant_location"
                  :required="false"
                  :google-api-key="googleMapsKey"
                  :show-internal-label="false"
                />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-checkbox
                v-model="form.is_default"
                :value="true"
                :unchecked-value="false"
                style="margin-top:27px"
              >
                Is Default Address
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Contact No."
                label-for="h-contactNo"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="contactNo"
                  rules="required"
                >
                  <b-form-input
                    id="contactNo"
                    v-model="form.contactNo"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the contact no."
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Email"
                label-for="h-email"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="email"
                  rules="required"
                >
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the email"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- submit and reset -->
          <b-row>
            <b-col
              offset-md="4"
            >
              <div>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  class="mr-1 custom-button-color"
                >
                  Submit
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="reset"
                  variant="outline-secondary"
                >
                  Clear
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BRow,
  BOverlay,
  BFormCheckbox,
  VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import BaseLocationPicker from '@/components/BaseLocationPicker.vue'

const ResourceRepository = RepositoryFactory.get('resource')
const MerchantRepository = RepositoryFactory.get('merchant')

function initialState() {
  return {
    open: false,
    id: null,
    form: {
      merchant_location: null,
      addressType: null,
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      postalCode: '',
      country: null,
      state: null,
      city: null,
      contactNo: '',
      email: '',
      is_default: false,
    },
    address_id: '',
    modalLoading: false,
    loading: false,
    cityOptions: [],
    stateOptions: [],
    countryOptions: [],
    addressTypeOptions: [],
    googleMapsKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '',
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BRow,
    BOverlay,
    BFormCheckbox,
    'v-select': vSelect,
    BaseLocationPicker,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  methods: {
    async openModal(merchantId, businessId) {
      await Object.assign(this.$data, initialState())
      this.open = true
      this.merchantId = merchantId
      this.businessId = businessId
      this.modalLoading = true
      await this.fetchCountryList()
      await this.fetchAddressType()
      this.modalLoading = false
    },
    async fetchCityList() {
      try {
        this.loading = true
        const { data } = (await ResourceRepository.getCityDropdownListMultilingual(this.form.state)).data
        this.cityOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async fetchStateList() {
      try {
        const { data } = (await ResourceRepository.getStateDropdownList(this.form.country)).data
        this.stateOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchCountryList() {
      try {
        const { data } = (await ResourceRepository.getCountryList()).data
        this.countryOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async onClickCreateMerchantBusinessAddress() {
      this.modalLoading = true
      try {
        // Format merchant location in the specified JSON format
        let merchantLocationData = null
        if (this.form.merchant_location) {
          merchantLocationData = {
            source: this.form.merchant_location.source || 'map',
            address: this.form.merchant_location.address || '',
            latitude: this.form.merchant_location.latitude || null,
            longitude: this.form.merchant_location.longitude || null,
          }
        }

        const payload = {
          address_details: {
            address_line_1: this.form.addressLine1,
            address_line_2: this.form.addressLine2,
            address_line_3: this.form.addressLine3,
            city_id: this.form.city,
            state_id: this.form.state,
            phone: this.form.contactNo,
            country_id: this.form.country,
            postal_code: this.form.postalCode,
            merchant_location: merchantLocationData,
            type: this.form.addressType.slug,
            address_type_id: this.form.addressType.id,
            is_default: this.form.is_default,
          },
        }
        const res = await MerchantRepository.createMerchantAddress(this.businessId, payload)
        this.address_id = res.data.data.id
        if (res.status === 201) {
          const contactPayload = {
            contact_details: {
              phone: this.form.contactNo,
              email: this.form.email,
              is_default: true,
            },
          }
          const contactRes = await MerchantRepository.createMerchantContact(this.businessId, this.address_id, contactPayload)
          if (contactRes.status === 201) {
            this.showSuccessMessage('Merchant business address created successfully')
            this.open = false
            this.$parent.onClickRefresh()
          } else {
            this.showErrorMessage('Sorry for the inconvenience! Please Try Again!!1')
          }
        } else {
          this.showErrorMessage('Sorry for the inconvenience! Please Try Again!!2')
        }
        this.open = false
        this.$parent.onClickRefresh()
      } catch (e) {
        this.showErrorMessage('Sorry for the inconvenience! Please Try Again!!3')
      }
      this.modalLoading = false
    },
    async fetchAddressType() {
      try {
        const { data } = (await MerchantRepository.getAddressType()).data
        this.addressTypeOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
  },
}
</script>
<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
@import '../../@core/scss/button-color.scss';
</style>
